import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Markdown, Modal } from '@Sage/hoi-poi-ui';

import { ConfigActions } from 'actions';
import {
    DANA_ACTIVATED_DISCLAIMER_BUTTON_BEHAVIOR,
    DANA_ACTIVATED_DISCLAIMER_BUTTON_BEHAVIOR_KEY,
    DANA_ACTIVATED_DISCLAIMER_KEY,
    resolveDanaDisclaimerButtonBehavior,
    shouldAutoPromptDanaDisclaimer,
} from 'constants/CopilotDisclaimer';
import { DANA_GOLDEN_MINUTE_DEBUG_TOOLS } from 'constants/Features';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

const useCopilot = ({ onConfirmDisclaimerSuccess, overrideModalOverlayClassName } = {}) => {
    // ========================================
    // HOOKS
    // ========================================
    const dispatch = useDispatch();

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const copilotActivated = useSelector(
        (state) => state.config.permission?.danaActivated || false,
    );
    const copilotActivatedDisclaimer = useSelector(
        (state) => state.config.permission?.[DANA_ACTIVATED_DISCLAIMER_KEY] || false,
    );
    const copilotDisclaimerButtonBehaviorRaw = useSelector(
        (state) => state.config.permission?.[DANA_ACTIVATED_DISCLAIMER_BUTTON_BEHAVIOR_KEY],
    );
    const hasCopilotDebugTools = useSelector(
        (state) => state.config?.flagsmith?.[DANA_GOLDEN_MINUTE_DEBUG_TOOLS] || false,
    );

    // ========================================
    // LOCAL STATE & REFS
    // ========================================
    const [copilotModalAfterDisclaimer, setCopilotModalAfterDisclaimer] = useState(null);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const copilotDisclaimerButtonBehavior = useMemo(
        () =>
            resolveDanaDisclaimerButtonBehavior(
                copilotActivatedDisclaimer,
                copilotDisclaimerButtonBehaviorRaw,
            ),
        [copilotActivatedDisclaimer, copilotDisclaimerButtonBehaviorRaw],
    );

    const shouldAutoPromptCopilotDisclaimer = useMemo(
        () =>
            shouldAutoPromptDanaDisclaimer({
                disclaimer: copilotActivatedDisclaimer,
                buttonBehavior: copilotDisclaimerButtonBehavior,
            }),
        [copilotActivatedDisclaimer, copilotDisclaimerButtonBehavior],
    );

    const copilotState = useMemo(
        () => ({
            copilotActivated,
            copilotActivatedDisclaimer,
            copilotDisclaimerButtonBehavior,
            hasCopilotDebugTools,
            shouldAutoPromptCopilotDisclaimer,
        }),
        [
            copilotActivated,
            copilotActivatedDisclaimer,
            copilotDisclaimerButtonBehavior,
            hasCopilotDebugTools,
            shouldAutoPromptCopilotDisclaimer,
        ],
    );

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const syncDisclaimerPermissions = useCallback(
        (accepted) => {
            dispatch(
                ConfigActions.updateSpecificPermissions([
                    { key: DANA_ACTIVATED_DISCLAIMER_KEY, value: accepted },
                    {
                        key: DANA_ACTIVATED_DISCLAIMER_BUTTON_BEHAVIOR_KEY,
                        value: DANA_ACTIVATED_DISCLAIMER_BUTTON_BEHAVIOR.NOT_ACTIVE,
                    },
                ]),
            );
        },
        [dispatch],
    );

    const persistDisclaimerDecision = useCallback(
        (accepted) => {
            return dispatch(
                ConfigActions.setDisclaimer(
                    { key: DANA_ACTIVATED_DISCLAIMER_KEY, value: accepted },
                    false,
                ),
            ).then((response) => {
                if (response.State === '1') {
                    syncDisclaimerPermissions(accepted);
                    return response;
                }
                throw new Error('dana-disclaimer-failed');
            });
        },
        [dispatch, syncDisclaimerPermissions],
    );

    // X / overlay / Esc: dismiss only — do not persist a deny decision
    const onDismissDisclaimer = useCallback(() => {
        setCopilotModalAfterDisclaimer(null);
    }, []);

    // Explicit Deny button: persist accepted=false + ButtonBehavior notactive
    const onCancelDisclaimer = useCallback(() => {
        persistDisclaimerDecision(false)
            .then(() => {
                setCopilotModalAfterDisclaimer(null);
            })
            .catch(() => {
                errorToast({ text: getLiteral('label_dana_disclaimer_error') });
                setCopilotModalAfterDisclaimer(null);
            });
    }, [persistDisclaimerDecision]);

    const onConfirmDisclaimer = useCallback(() => {
        persistDisclaimerDecision(true)
            .then(() => {
                onConfirmDisclaimerSuccess &&
                    onConfirmDisclaimerSuccess({ copilotModalAfterDisclaimer });
                setCopilotModalAfterDisclaimer(null);
            })
            .catch(() => {
                errorToast({ text: getLiteral('label_dana_disclaimer_error') });
                setCopilotModalAfterDisclaimer(null);
            });
    }, [copilotModalAfterDisclaimer, onConfirmDisclaimerSuccess, persistDisclaimerDecision]);

    // Kept for backwards compatibility with consumers that may call it
    const copilotDisclaimerOnClose = useCallback(() => {
        // Permission is now updated immediately in persistDisclaimerDecision
        // This is a no-op but kept for API compatibility
    }, []);

    // ========================================
    // RENDER HELPERS
    // ========================================
    const renderCopilotDisclaimer = useMemo(
        () => (
            <Modal
                cancelText={getLiteral('label_danai_button_deny_tos')}
                className="fm-entity-modal__dana_disclaimer"
                confirmText={getLiteral('label_danai_button_accept_tos')}
                isOpen={!!copilotModalAfterDisclaimer}
                onCancel={onCancelDisclaimer}
                onConfirm={onConfirmDisclaimer}
                onRequestClose={onDismissDisclaimer}
                overlayClassName={overrideModalOverlayClassName}
                title={getLiteral('label_danai_title_accept_tos')}
            >
                <Markdown
                    content={getLiteral('label_danai_text_accept_tos')}
                    overrides={{ a: { target: '_blank' } }}
                />
            </Modal>
        ),
        [
            copilotModalAfterDisclaimer,
            onCancelDisclaimer,
            onConfirmDisclaimer,
            onDismissDisclaimer,
            overrideModalOverlayClassName,
        ],
    );

    return {
        ...copilotState,
        copilotDisclaimerOnClose,
        renderCopilotDisclaimer,
        setCopilotModalAfterDisclaimer,
    };
};

export default useCopilot;
